"use client";

import { useCallback } from "react";
import { useSendMessage, useMessageAction } from "@/lib/hooks/api/messages";
import { useConnections } from "@/lib/contexts/ConnectionsContext";
import { toast } from "sonner";
import { Message } from "@/lib/core/crm/types";

export interface ComposeMessageValues {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  fromAccount: string;
  attachments?: Array<{
    id: string;
    filename: string;
    contentType: string;
    content: string;
    size: number;
  }>;
  /** Opt in to open tracking for this email (per-email checkbox). */
  trackOpens?: boolean;
}

// Function to process HTML and extract inline images and attachments
async function processInlineImages(htmlContent: string): Promise<{
  processedHtml: string;
  attachments: Array<{
    filename: string;
    content: string;
    contentType: string;
    cid: string;
  }>;
}> {
  const attachments: Array<{
    filename: string;
    content: string;
    contentType: string;
    cid: string;
  }> = [];

  // Find all img tags in the HTML
  const imgRegex = /<img[^>]+src="([^"]+)"[^>]*>/gi;
  let processedHtml = htmlContent;
  let match;

  // Process images - keep Supabase URLs as-is for email sending
  while ((match = imgRegex.exec(htmlContent)) !== null) {
    const imgSrc = match[1];

    // For Supabase storage images, keep the original URL
    // The email provider should be able to access these public URLs directly
    if (imgSrc.includes("/storage/v1/object/public/public/email-images/")) {
      console.log("Keeping Supabase image URL for email:", imgSrc);
      // No processing needed - keep the original URL
    } else if (imgSrc.startsWith("cid:")) {
      // Skip already processed cid references
      console.log("Skipping cid reference:", imgSrc);
    } else if (imgSrc.startsWith("data:")) {
      // Convert data URLs to attachments if needed
      try {
        const base64Data = imgSrc.split(",")[1];
        const contentType = imgSrc.split(";")[0].split(":")[1];

        // Generate a content ID
        const cid = `image_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        attachments.push({
          filename: `image_${cid}.${contentType.split("/")[1]}`,
          content: base64Data,
          contentType,
          cid,
        });

        // Replace the data URL with cid reference
        processedHtml = processedHtml.replace(imgSrc, `cid:${cid}`);
      } catch (error) {
        console.error("Failed to process data URL image:", error);
      }
    }
  }

  return { processedHtml, attachments };
}

// Helper function to convert blob to base64
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function useComposeMessageSend(
  onSuccess?: () => void,
  originalMessage?: Message | null,
  isForwardMode?: boolean
) {
  const sendMessage = useSendMessage();
  const messageAction = useMessageAction();
  const { connections } = useConnections();

  const handleSendMessage = useCallback(
    async (values: ComposeMessageValues) => {
      console.log("🚀 useComposeMessageSend.handleSendMessage called with values:", values);
      console.log("🔍 Available connections:", connections);

      // Find the selected connection to get provider info
      const selectedConnection = connections.find(
        (conn) => conn.connectionId === values.fromAccount
      );
      console.log("🔍 Selected connection:", selectedConnection);
      console.log("🔍 Looking for connectionId:", values.fromAccount);

      if (!selectedConnection) {
        console.error("❌ No selected connection found");
        console.error(
          "❌ Available connectionIds:",
          connections.map((c) => c.connectionId)
        );
        toast.error("Please select a valid account to send from.");
        return;
      }

      try {
        // Process inline images in the email body
        const { processedHtml, attachments: inlineAttachments } = await processInlineImages(
          values.body
        );

        // Combine inline attachments with Google Drive attachments
        const allAttachments = [...inlineAttachments];

        // Add Google Drive and device file attachments if they exist
        if (values.attachments && values.attachments.length > 0) {
          const fileAttachments = values.attachments.map((attachment) => ({
            filename: attachment.filename,
            content: attachment.content,
            contentType: attachment.contentType,
            cid: `attachment_${attachment.id}`,
          }));
          allAttachments.push(...fileAttachments);
        }

        // If forwarding, use the forward API
        if (isForwardMode && originalMessage) {
          // Parse recipients from the "to" field (comma-separated emails)
          const toRecipients = values.to
            .split(",")
            .map((email) => email.trim())
            .filter((email) => email.length > 0);

          console.log("📤 Forwarding message with mutation:", {
            provider: selectedConnection.provider,
            connectionId: values.fromAccount,
            messageId: originalMessage.providerMessageId,
            toRecipients,
            body: processedHtml,
          });

          messageAction.mutate(
            {
              provider: selectedConnection.provider,
              messageId: originalMessage.providerMessageId,
              connectionId: values.fromAccount,
              action: "forward",
              payload: {
                to: toRecipients,
                body: processedHtml,
              },
            },
            {
              onSuccess: () => {
                console.log("✅ Message forwarded successfully, calling onSuccess callback");
                onSuccess?.();
              },
            }
          );
        } else {
          // Regular send
          console.log("📤 Sending message with mutation:", {
            provider: selectedConnection.provider,
            connectionId: values.fromAccount,
            to: values.to,
            cc: values.cc,
            bcc: values.bcc,
            subject: values.subject,
            body: processedHtml,
            attachments: allAttachments.length > 0 ? allAttachments : undefined,
          });

          // Reply threading: when replying (an originalMessage is present and we're
          // not forwarding), carry its RFC Message-ID so the send route adds
          // In-Reply-To/References. The provider ignores a non-RFC id, so passing
          // providerMessageId is safe even when it's a provider-internal id.
          const replyInReplyTo =
            !isForwardMode && originalMessage?.providerMessageId
              ? originalMessage.providerMessageId
              : undefined;

          sendMessage.mutate(
            {
              provider: selectedConnection.provider,
              connectionId: values.fromAccount,
              to: values.to,
              cc: values.cc,
              bcc: values.bcc,
              subject: values.subject,
              body: processedHtml,
              attachments: allAttachments.length > 0 ? allAttachments : undefined,
              trackOpens: values.trackOpens,
              inReplyTo: replyInReplyTo,
            },
            {
              onSuccess: () => {
                console.log("✅ Message sent successfully, calling onSuccess callback");
                onSuccess?.();
              },
            }
          );
        }
        console.log("✅ Mutation called successfully");
      } catch (error) {
        console.error("❌ Error calling mutation:", error);
        toast.error("Failed to process message for sending");
      }
    },
    [sendMessage, messageAction, connections, onSuccess, isForwardMode, originalMessage]
  );

  return {
    handleSendMessage,
    isSending: isForwardMode ? messageAction.isPending : sendMessage.isPending,
    error: isForwardMode ? messageAction.error : sendMessage.error,
  };
}
